// Shared Web Audio mixer engine + React context.
// Owns a single AudioContext, master gain, per-deck channel gains (for
// crossfading), and a MediaRecorder tapped off the master for set recording.

import React, { createContext, useContext, useEffect, useRef, useState } from "react";

const MixerContext = createContext(null);

class MixerEngine {
  constructor() {
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.8;

    // Master analyser for VU / master waveform (optional future use)
    this.masterAnalyser = this.ctx.createAnalyser();
    this.masterAnalyser.fftSize = 256;

    // Recording tap
    this.recDest = this.ctx.createMediaStreamDestination();

    this.masterGain.connect(this.masterAnalyser);
    this.masterAnalyser.connect(this.ctx.destination);
    this.masterAnalyser.connect(this.recDest);

    // Per-deck channel gains
    this.channels = {};
    ["A", "B", "C", "D"].forEach(id => {
      const g = this.ctx.createGain();
      g.gain.value = 1;
      g.connect(this.masterGain);
      this.channels[id] = g;
    });

    this._crossfader = 0.5;
    this.setCrossfader(0.5);

    this.recorder = null;
    this.recChunks = [];
    this.recStartedAt = null;
  }

  async resume() {
    if (this.ctx.state === "suspended") await this.ctx.resume();
  }

  setMasterVolume(v) { this.masterGain.gain.value = v; }

  setCrossfader(pos) {
    this._crossfader = pos;
    // Equal-power crossfade curve — A/C left, B/D right
    const gAC = Math.cos(pos * Math.PI / 2);
    const gBD = Math.cos((1 - pos) * Math.PI / 2);
    this.channels.A.gain.value = gAC;
    this.channels.C.gain.value = gAC;
    this.channels.B.gain.value = gBD;
    this.channels.D.gain.value = gBD;
  }

  channelInput(id) { return this.channels[id]; }

  startRecording() {
    if (this.recorder && this.recorder.state === "recording") return false;
    this.recChunks = [];
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : "audio/webm";
    this.recorder = new MediaRecorder(this.recDest.stream, { mimeType: mime });
    this.recorder.ondataavailable = e => { if (e.data && e.data.size > 0) this.recChunks.push(e.data); };
    this.recorder.start(1000);
    this.recStartedAt = Date.now();
    return true;
  }

  stopRecording() {
    return new Promise(resolve => {
      if (!this.recorder || this.recorder.state === "inactive") { resolve(null); return; }
      this.recorder.onstop = () => {
        const blob = new Blob(this.recChunks, { type: this.recorder.mimeType });
        this.recStartedAt = null;
        resolve(blob);
      };
      this.recorder.stop();
    });
  }
}

export const MixerProvider = ({ children }) => {
  const engineRef = useRef(null);
  const [ready, setReady] = useState(false);
  // `mixerError` surfaces AudioContext-creation failures (mobile Safari
  // strict mode, embedded WebViews without Web Audio, browsers that hit
  // the ~6-AudioContext cap). Deck.jsx renders a red banner + toast so
  // the user actually sees why Play is doing nothing — the previous
  // silent `return` at the top of togglePlay was mistaken for a freeze.
  const [mixerError, setMixerError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recElapsed, setRecElapsed] = useState(0);

  // Construct the engine in an effect (NOT during render) so React 18
  // Strict Mode's double-invoke doesn't create two AudioContexts on mount.
  useEffect(() => {
    if (engineRef.current || typeof window === "undefined") return;
    try {
      engineRef.current = new MixerEngine();
      setReady(true);
    } catch (e) {
      // Common causes: Web Audio disabled in an embedded WebView, or the
      // browser has hit its per-tab AudioContext cap (Chrome ~6). We keep
      // `ready=false` so togglePlay bails, but now we surface WHY.
      console.error("MixerEngine init failed:", e);
      setMixerError(e?.message || "Audio engine could not start on this browser");
    }
  }, []);

  useEffect(() => {
    if (!isRecording) return;
    const iv = setInterval(() => {
      const t = engineRef.current?.recStartedAt;
      if (t) setRecElapsed(Math.floor((Date.now() - t) / 1000));
    }, 500);
    return () => clearInterval(iv);
  }, [isRecording]);

  const startRec = async () => {
    await engineRef.current.resume();
    if (engineRef.current.startRecording()) {
      setIsRecording(true);
      setRecElapsed(0);
    }
  };

  const stopRec = async () => {
    const blob = await engineRef.current.stopRecording();
    setIsRecording(false);
    setRecElapsed(0);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `neuraldeck-set-${ts}.webm`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
  };

  return (
    <MixerContext.Provider value={{ engine: engineRef.current, ready, mixerError, isRecording, recElapsed, startRec, stopRec }}>
      {children}
    </MixerContext.Provider>
  );
};

export const useMixer = () => useContext(MixerContext);
